/**
 * Module to add Pristine localization so it can be used across the app.
 */

import Pristine from 'pristinejs';
import { getPageLang } from './utils';

/**
 * Add a custom validator for phone numbers.
 * Usage:
 * Add the attribute data-pristine-phone="{{ t('forms.errors.phone') }}" to the input element.
 * https://github.com/sha256/Pristine#add-a-global-custom-validator
 */
Pristine.addValidator('phone', function(value: string) {
  console.debug('Validating phone', value);
  if (!value || value.trim() === '') {
    // Don't validate empty values; let required/required-any handle it
    return true;
  }
  const PHONE_REGEX = /^\+?[1-9]\d{1,14}$|^(?:\(?\+?[1-9]{1,4}?\)?)?(?:[-.\s()]?\d{1,4}[-.\s()]?){3,4}$/;
  return PHONE_REGEX.test(value);
  // The ${1} is the first param in the data attribute, which we're hijacking to provide the localized error message
}, '${1}', 1, true);

/**
 * Add a custom validator for conditional required fields.
 * Usage:
 * Add the attribute data-pristine-required-if="fieldNameOrSelector" to the input element.
 */
Pristine.addValidator('required-if', function(value: string, ...param: string[]) {
  console.debug('Validating required-if', value, param);

  // Pull out the second param since the first is the localized error message
  const theParam = param[1]?.trim();

  // First, try to find the related field by name
  let relatedField = document.querySelector(`[name="${theParam}"]`);

  // If not found, treat param as a full CSS selector
  if (!relatedField) {
    relatedField = document.querySelector(theParam);
  }

  if (!relatedField) {
    console.warn('Related field not found for required-if validator:', theParam);
    return true; // If the related field doesn't exist, pass validation
  }

  const relatedValue = relatedField['value']?.toString() || '';
  if (relatedValue && relatedValue.trim() !== '') {
    return value && value.trim() !== ''; // Field is required if related field has a value
  }

  return true; // Field is not required if related field is empty
  // The ${1} is the first param in the data attribute, which we're hijacking to provide the localized error message
}, '${1}', 2, true);

/**
 * Add a custom validator for fields where at least one of the specified fields must have a value.
 * Usage:
 * Add the attribute data-pristine-required-any="nameOrSelector,nameOrSelector2" to the input element.
 */
Pristine.addValidator('required-any', function(_value: string, ...param: string[]) {
  // console.debug('Validating required-any', value, param);

  // Strip out the first param since the first is the localized error message
  param = param.slice(1);

  // Split the parameter into multiple field names or selectors
  const fields = param.map((field) => field.trim());
  // console.debug('Fields to check:', fields);

  // Check if at least one of the fields has a value
  const hasValue = fields.some((field) => {
    let relatedField = document.querySelector(`[name="${field}"]`) || document.querySelector(field);
    if (relatedField) {
      const relatedValue = relatedField['value']?.toString() || '';
      return relatedValue.trim() !== '';
    }
    return false;
  });

  return hasValue; // Pass validation if at least one field has a value
  // The ${1} is the first param in the data attribute, which we're hijacking to provide the localized error message
}, '${1}', 2, true);

/**
 * Add additional localization messages for other languages
 * https://github.com/sha256/Pristine/blob/master/src/lang.js
 */
// TODO: Need translations for these messages
Pristine.addMessages('fr', {
  // required: 'This field is required',
  // email: 'This field requires a valid e-mail address',
  // number: 'This field requires a number',
  // integer: 'This field requires an integer value',
  // url: 'This field requires a valid website URL',
  // tel: 'This field requires a valid telephone number',
  // maxlength: 'This fields length must be < ${1}',
  // minlength: 'This fields length must be > ${1}',
  // min: 'Minimum value for this field is ${1}',
  // max: 'Maximum value for this field is ${1}',
  // pattern: 'Please match the requested format',
  // equals: 'The two fields do not match',
  // default: 'Please enter a correct value',

  required: 'Ce champ est obligatoire',
  email: 'Ce champ nécessite une adresse courriel valable',
  number: 'Ce champ nécessite un nombre.',
  integer: 'Ce champ nécessite une valeur entière.',
  url: 'Ce champ requiert une URL de site Web valide.',
  // It looks like the `tel` validator has a message defined, but is not implemented in the pristinejs library
  // tel: '(FR) This field requires a valid telephone number',
  maxlength: 'Ce champ doit avoir une longueur inférieure à ${1}.',
  minlength: 'Ce champ doit avoir une longueur supérieure à ${1}.',
  min: 'La valeur minimale de ce champ est ${1}.',
  max: 'La valeur maximale de ce champ est ${1}.',
  pattern: 'Veuillez respecter le format demandé.',
  equals: 'Les deux champs ne correspondent pas.',
  default: 'Veuillez entrer une valeur correcte.',
  // Our custom phone validator - doesn't seem to read from the locale...
  // Also doesn't seem to read `data-pristine-phone-message` attribute either
  // So to use it, we're passing in the localized message in `data-pristine-phone="{{ t('forms.errors.phone') }}"`
  // phone: '(FR) This field requires a valid telephone number',
});

// Pristine.addMessages('en', {
//   phone: '(EN) This field requires a valid telephone number',
// });

const lang = getPageLang();
console.debug('Setting Pristine locale to', lang);
Pristine.setLocale(lang);

export default Pristine;
